import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { MOBILE_NAV_ANIM, NAV_DESKTOP } from '../../../../theme/tokens';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import type { FeatureFlagKey } from '../../../../config/featureFlags';
import { mnavMoreTarget, mnavTarget, type FtuxTargetId } from '../../../../features/ftux/targets';
import { useMoreHeld } from '../../../../features/ftux/shell/moreHeld';
import {
  isDoorShown,
  PHONE_BAR_DOORS,
  PHONE_DRAWER_DOORS,
  SHOWCASE_HIDDEN_DOORS,
  STUDIO_DOORS,
  type PhoneDrawerDoorKey,
} from '../../studioMenu';
import { MaskIcon } from '../MaskIcon';
import { WalletGlyph } from '../WalletGlyph';
import { PlayersGlyph } from '../PlayersGlyph';
import { MOBILE_NAV_DRAWER_ATTR, MOBILE_NAV_HIDDEN_ATTR } from '../../mobileNavVisibility';
import { BAR_ICON, MORE_ICON } from './barIcons';
import { useHideOnScroll } from './useHideOnScroll';
import {
  DrawerOverlay,
  DrawerMenu,
  DrawerItem,
  DrawerLabel,
  DrawerIconBubble,
  ScrimBackfill,
  PageShadow,
  BottomMenu,
  BottomItem,
  BottomToggle,
  IconWrapper,
  ItemLabel,
  IN_MORE_ATTR,
  type DrawerPhase,
} from './MobileNav.styles';

/**
 * Mobile navigation — converted from the `oh.module.studio-navigation`
 * template: the fixed bottom pill menu (Home · Stats · Marketing · Website ·
 * More) plus the blurred "More" drawer (Product · Acquisition · Operations ·
 * Players · Wallet · Settings) with the staggered open/close animation. The
 * open → closing → closed phase machine reproduces the module's
 * isOpen/isClosing dataset driving the template's CSS states. Lumio is not on the
 * bar: the header's Lumio button opens it on phones (see TopBar). Pulse is a tab of
 * Stats, so it has no door of its own. (Website settings moved under Settings →
 * `/account/settings/website-settings`.)
 */
const CDN = 'https://micros-t.b-cdn.net';

type DrawerEntry = {
  key: PhoneDrawerDoorKey;
  href: string;
  /** Remote SVG, rendered via MaskIcon (CSS mask, recoloured by background-color). */
  icon?: string;
  /** Local inline glyph — takes precedence over `icon` (e.g. the wallet). */
  glyph?: React.ReactElement;
  /** Settings gear gets the rotate transition class. */
  gear?: boolean;
  /** Build-ahead feature this entry belongs to; the drawer drops it while the
   *  flag is off (see `config/featureFlags`). Absent = always in the drawer. */
  flag?: FeatureFlagKey;
};

/** Each More drawer row's look. */
const DRAWER_LOOK: Readonly<Record<PhoneDrawerDoorKey, Pick<DrawerEntry, 'icon' | 'glyph' | 'gear'>>> = {
  product: { icon: `${CDN}/studio-menu-icon-product-1779968968262.svg` },
  acquisition: { icon: `${CDN}/studio-menu-icon-acquisition-1779968968261.svg` },
  operations: { icon: `${CDN}/studio-menu-icon-operations-1779968968260.svg` },
  players: { glyph: <PlayersGlyph className="drawer-icon" /> },
  wallet: { glyph: <WalletGlyph className="drawer-icon" /> },
  settings: { icon: `${CDN}/studio-menu-icon-settings-1779968968253.svg`, gear: true },
};

/** The More drawer's rows: the studio's menu (studioMenu.ts), top to bottom. */
const DRAWER_ITEMS: DrawerEntry[] = PHONE_DRAWER_DOORS.map((key) => {
  const door = STUDIO_DOORS[key];
  return { key, href: door.href, flag: door.flag, ...DRAWER_LOOK[key] };
});

export type MobileNavProps = {
  /** While the Lumio overlay is open, the bar crossfades out (the overlay's input
   *  pill blooms in on the same footprint). */
  lumioActive?: boolean;
  /** The Newton showcase is on: the More drawer leaves out its SHOWCASE_HIDDEN_DOORS (the Wallet row). */
  showcase?: boolean;
};

/** Where the FTUX finds the bar and its More (its doors carry `mnav-<key>`, the drawer rows `mnav-more-<key>`). Inert
 *  outside an FTUX step. */
const BAR_TARGET: FtuxTargetId = 'mnav-bar';
const MORE_TARGET: FtuxTargetId = 'mnav-more';

export const MobileNav = ({ lumioActive = false, showcase = false }: MobileNavProps): React.ReactElement => {
  const { t } = useTranslation();

  // Translate the "More" drawer labels at the render site (the literals below are what the extractor harvests).
  // Uppercase to mirror the template copy. Keyed by the door, so a row added to the studio's menu (studioMenu.ts)
  // fails the typecheck until its label is here: never a row left in English in the other languages.
  const drawerLabels: Readonly<Record<PhoneDrawerDoorKey, string>> = {
    product: t('PRODUCT'),
    acquisition: t('ACQUISITION'),
    operations: t('OPERATIONS'),
    players: t('PLAYERS'),
    wallet: t('WALLET'),
    settings: t('SETTINGS'),
  };

  // The drawer as it actually stands, gated entries dropped — the same gate the
  // desktop rail uses, because this is the second door into the same sections.
  const gate = useFeatureGate();
  const drawerItems = React.useMemo(
    () =>
      DRAWER_ITEMS.filter((entry) => gate.isEnabled(entry.flag) && !(showcase && SHOWCASE_HIDDEN_DOORS.includes(entry.key))),
    [gate, showcase],
  );
  const barDoors = React.useMemo(
    () => PHONE_BAR_DOORS.filter((key) => isDoorShown(key, (flag) => gate.isEnabled(flag))),
    [gate],
  );

  const [phase, setPhase] = React.useState<DrawerPhase>('closed');
  const closeTimer = React.useRef<number | null>(null);

  // "More" drawer scroll fades — toggled per edge so a long page list dissolves
  // into the blur instead of looking hard-cut (mirrors the desktop flyout).
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const [fadeTop, setFadeTop] = React.useState(false);
  const [fadeBottom, setFadeBottom] = React.useState(false);
  const updateDrawerFades = React.useCallback(() => {
    const el = drawerRef.current;
    if (!el) return;
    setFadeTop(el.scrollTop > 1);
    setFadeBottom(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);
  React.useEffect(() => {
    if (phase === 'closed') return undefined;
    updateDrawerFades();
    // Re-measure once the drawer has laid out (post-open) and on resize.
    const id = window.setTimeout(updateDrawerFades, 0);
    window.addEventListener('resize', updateDrawerFades);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('resize', updateDrawerFades);
    };
  }, [phase, updateDrawerFades]);

  // Hide-on-scroll: the bar slides out (scrim fades) as the user scrolls DOWN and
  // returns on scroll-up. Only on mobile (the bar's breakpoint) and only when the
  // user hasn't asked for reduced motion. Forced visible whenever the "More"
  // drawer is open/closing, so the pill never slips away mid-interaction (and the
  // user can always reach it to scroll up).
  const isDesktop = useMediaQuery(NAV_DESKTOP);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const scrolledHidden = useHideOnScroll(!isDesktop && !reduceMotion);
  const barHidden = scrolledHidden && phase === 'closed';

  // Publish that state for page chrome resting above the pill (see
  // mobileNavVisibility), and clear it on unmount so it never outlives the layout.
  React.useEffect(() => {
    const root = document.documentElement;
    root.toggleAttribute(MOBILE_NAV_HIDDEN_ATTR, barHidden);
    return () => root.removeAttribute(MOBILE_NAV_HIDDEN_ATTR);
  }, [barHidden]);
  // And whether the drawer covers the page, for chrome raised over the scrim (see mobileNavVisibility).
  const drawerShown = phase !== 'closed';
  React.useEffect(() => {
    const root = document.documentElement;
    root.toggleAttribute(MOBILE_NAV_DRAWER_ATTR, drawerShown);
    return () => root.removeAttribute(MOBILE_NAV_DRAWER_ATTR);
  }, [drawerShown]);

  const clearTimer = React.useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  React.useEffect(() => clearTimer, [clearTimer]);

  const close = React.useCallback(() => {
    clearTimer();
    setPhase('closing');
    closeTimer.current = window.setTimeout(() => {
      setPhase('closed');
      closeTimer.current = null;
    }, MOBILE_NAV_ANIM.closeTotalMs);
  }, [clearTimer]);

  // The FTUX's phone map holds the drawer open while Lumio tells the parts in it: it opens with its usual stagger,
  // stays while held (its own toggles do nothing then) and closes the usual way once let go. Never held outside the
  // FTUX, so the drawer otherwise behaves as before.
  const held = useMoreHeld();
  const openedByHold = React.useRef(false);
  React.useEffect(() => {
    if (held) {
      clearTimer();
      setPhase('open');
      openedByHold.current = true;
    } else if (openedByHold.current) {
      openedByHold.current = false;
      close();
    }
  }, [held, clearTimer, close]);

  /* Template `links.onToggle` on the More item + `onOverlayClick` on the overlay. */
  const onMoreClick = React.useCallback(() => {
    if (held) return;
    if (phase === 'open') {
      close();
    } else {
      clearTimer();
      setPhase('open');
    }
  }, [held, phase, close, clearTimer]);

  // Highlight "More" (orange) when the current page lives in the More drawer (i.e.
  // it isn't one of the bottom-bar's own items), so the active section still shows.
  const { pathname } = useLocation();
  const moreActive = drawerItems.some(
    (entry) => pathname === entry.href || pathname.startsWith(`${entry.href}/`),
  );
  // While the drawer is open the owner is in More: More is the door lit (orange), not the page's own door on the bar
  // (the user, 2026-10-23); the page's door lights again as the drawer closes.
  const inMore = phase === 'open';

  return (
    <>
      <PageShadow $hidden={barHidden} $lumioActive={lumioActive} aria-hidden={lumioActive || undefined}>
        <ScrimBackfill $hidden={barHidden} />
        <BottomMenu
          $hidden={barHidden}
          $lumioActive={lumioActive}
          data-ftux={BAR_TARGET}
          {...{ [IN_MORE_ATTR]: inMore || undefined }}
        >
          {barDoors.map((key) => (
            <BottomItem key={key} to={STUDIO_DOORS[key].href} data-ftux={mnavTarget(key)}>
              <IconWrapper>{BAR_ICON[key]}</IconWrapper>
              <ItemLabel className="txt" text={STUDIO_DOORS[key].label} />
            </BottomItem>
          ))}
          <BottomToggle
            onClick={onMoreClick}
            className={moreActive || inMore ? 'active' : undefined}
            aria-current={moreActive ? 'page' : undefined}
            data-ftux={MORE_TARGET}
          >
            <IconWrapper>
              {MORE_ICON}
            </IconWrapper>
            <ItemLabel className="txt" text="More" />
          </BottomToggle>
        </BottomMenu>
      </PageShadow>

      <DrawerOverlay $phase={phase} onClick={held ? undefined : close}>
        <DrawerMenu ref={drawerRef} onScroll={updateDrawerFades} $fadeTop={fadeTop} $fadeBottom={fadeBottom}>
          {drawerItems.map((entry) => (
            <DrawerItem key={entry.href} to={entry.href} data-ftux={mnavMoreTarget(entry.key)}>
              <DrawerLabel text={drawerLabels[entry.key]} />
              <DrawerIconBubble>
                {entry.glyph ?? (
                  <MaskIcon
                    className={entry.gear ? 'drawer-icon-mask settings-gear' : 'drawer-icon-mask'}
                    src={entry.icon ?? ''}
                  />
                )}
              </DrawerIconBubble>
            </DrawerItem>
          ))}
        </DrawerMenu>
      </DrawerOverlay>
    </>
  );
};

export default MobileNav;
